import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  monitoringJustifiedService,
  mapMonitoringJustifiedPage,
  type LC_ListMonitoringJustifiedIn,
  type LC_MonitoringFiltersIn,
  type MonitoringJustified,
} from '@/api/domains/attendance/monitoring';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type {
  PaginatedFetchParams,
  PaginatedFetchResult,
  PaginatedListSort,
} from '@/hooks/ui/usePaginatedList';
import { useUI } from '@/hooks/ui/useUI';


export interface UseMonitoringJustifiedResult {
  rows: MonitoringJustified[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  loading: boolean;
  /** `true` desde que el backend responde por primera vez. */
  hasLoadedOnce: boolean;
  onSearch: (text: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  refetch: () => void;
}

const DEFAULT_SORT_KEY = 'fullName';

// Table emite direcciones en minúscula; el backend las espera en mayúscula. De ahí las dos
// versiones del mismo orden por defecto.
export const MONITORING_JUSTIFIED_DEFAULT_SORT = {
  key: DEFAULT_SORT_KEY,
  direction: 'asc',
} as const;
const INITIAL_SORT: PaginatedListSort = { key: DEFAULT_SORT_KEY, direction: 'ASC' };

// Columnas cuyo key difiere del campo orderBy del backend (MonitoringJustifiedOrderByFields);
// omitir alguna hace que el validador rechace la petición. `fullName` coincide en ambos lados.
const ORDER_BY_MAP: Record<string, string> = {
  rut: 'employeeTaxIdentificationNumber',
  statusCode: 'status',
};

/**
 * Listado paginado "Justificados" del Monitoreo de Asistencia. Búsqueda, orden y paginación son
 * server-side vía `usePaginatedList`; el fetch espera el primer payload del panel de filtros.
 */
export function useMonitoringJustified(
  appliedFilters: LC_MonitoringFiltersIn | null,
  refreshToken = 0,
): UseMonitoringJustifiedResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<LC_MonitoringFiltersIn | null>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<MonitoringJustified> | null> => {
      // En el render donde appliedFilters pasa de null a objeto, `enabled` ya es true pero el
      // filters interno de usePaginatedList sigue en null: sin esta guarda se consultaría el
      // endpoint sin filtros, o sea todo el holding.
      if (params.filters === null) {
        return { items: [], totalRecords: 0 };
      }

      const request: LC_ListMonitoringJustifiedIn = {
        ...params.filters,
        page: params.page,
        pageSize: params.pageSize,
        searchText: params.searchText ?? null,
        orderBy: params.sort?.key ?? null,
        orderDirection: params.sort?.direction ?? null,
      };

      const response = await monitoringJustifiedService.get(request, signal);

      if (!response.success || !response.data) {
        // Un error también cuenta como primera respuesta: ya no corresponde el skeleton.
        if (response.error?.code !== 'ABORTED') {
          showApiError(response, translate('attendance:monitoring.justified.loadError'));
          setHasLoadedOnce(true);
        }
        return null;
      }

      setHasLoadedOnce(true);
      return mapMonitoringJustifiedPage(response.data);
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    setFilters,
    refetch,
  } = usePaginatedList<MonitoringJustified, LC_MonitoringFiltersIn | null>({
    fetchFn,
    initialFilters: appliedFilters,
    initialSort: INITIAL_SORT,
    orderByMap: ORDER_BY_MAP,
    enabled: appliedFilters !== null,
  });

  useEffect(() => {
    setFilters(appliedFilters);
  }, [appliedFilters, setFilters]);

  // Reconsulta cuando cambia el token de refresco de la pantalla, conservando búsqueda, orden y
  // paginación; el token inicial no dispara un fetch extra.
  const previousRefreshTokenRef = useRef(refreshToken);
  useEffect(() => {
    if (previousRefreshTokenRef.current === refreshToken) return;
    previousRefreshTokenRef.current = refreshToken;
    refetch();
  }, [refreshToken, refetch]);

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    hasLoadedOnce,
    onSearch,
    onSortChange,
    refetch,
  };
}

export default useMonitoringJustified;
